/**
 * Einen Stammdatensatz öffnen und bearbeiten.
 *
 * Die Tabelle zeigt, was man beim Überfliegen braucht. Alles andere gehört
 * hierher — und zwar so, dass später neue Felder je eine Zeile kosten und
 * nicht einen neuen Bildschirm. Deshalb sind die Felder Daten und kein
 * Formularcode: Wer nächstes Jahr „Ansprechpartner" und „Zahlungsziel" am
 * Kunden braucht, trägt zwei Einträge in eine Liste ein.
 *
 * Gespeichert wird erst auf Knopfdruck, nicht bei jedem Tastendruck. Bei einer
 * Adresse tippt man mittendrin Unsinn — „Musterstr" ist auf dem Weg zu
 * „Musterstraße" ein Zustand, kein Wert. Zwischenstände gehören nicht in die
 * Datenbank und schon gar nicht in den Verlauf.
 *
 * ═══ IMMER MIT key={datensatz.id} EINBINDEN ═══
 *
 * Damit man tippen kann, hält die Tafel die Werte in eigenem Zustand. Der wird
 * nur beim ERSTEN Anzeigen aus den Eigenschaften befüllt — React behält
 * Zustand, solange dieselbe Instanz an derselben Stelle steht. Wechselt die
 * Seite bei offener Tafel auf einen anderen Datensatz, ist genau das der Fall:
 * Der Titel kommt frisch aus den Eigenschaften, die Felder zeigen weiter den
 * vorigen.
 *
 * Das verfälscht Daten. Wer dann speichert, schreibt die Werte von A in den
 * Datensatz B — und weil `geaendert` gegen die NEUEN Eigenschaften verglichen
 * wird, bietet die Tafel das von selbst als Änderung an. Die Standprüfung über
 * `geaendert_am` greift nicht: B ist unverändert, der Fehler sitzt im Browser.
 *
 * Der key löst das, weil React die Instanz dann verwirft und den Zustand neu
 * aufbaut. Er ist die ID — NICHT `id + geaendert_am`: Sonst würde jedes
 * Auffrischen im Hintergrund die halb getippte Eingabe wegwerfen. Gleichzeitige
 * Änderungen fängt `aendern()` ab, nicht der key.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Karte, Knopf, Meldung } from './bausteine';

export type Detailfeld = {
  schluessel: string;
  marke: string;
  art?: 'text' | 'auswahl' | 'zahl' | 'datum';
  optionen?: { wert: string; text: string }[];
  hinweis?: string;
  /** Berechnete Werte wie der Kurzname — anzeigen, nicht anfassen. */
  schreibgeschuetzt?: boolean;
};

export function Detailtafel(eigenschaften: {
  titel: ReactNode;
  felder: Detailfeld[];
  werte: Record<string, string>;
  /** Zusätzliche Angaben ohne Eingabefeld — Anlagedatum, Verwendungen, … */
  hinweise?: ReactNode;
  gesperrt?: string | null;
  laeuft?: boolean;
  schliessen: () => void;
  speichern: (geaendert: Record<string, string>) => void;
}) {
  const [werte, setWerte] = useState(eigenschaften.werte);

  const geaendert = Object.fromEntries(
    Object.entries(werte).filter(
      ([schluessel, wert]) => wert !== (eigenschaften.werte[schluessel] ?? ''),
    ),
  );
  const hatAenderung = Object.keys(geaendert).length > 0;

  // Die Tafel geht ÜBER der Tabelle auf: Wer weit unten eine Zeile anklickt, sähe
  // sonst nur, dass die Zeile grau wird (HJ-009). Der `key` je Datensatz baut sie
  // bei jedem Wechsel neu auf, also gilt das auch beim Wechsel.
  const anfang = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anfang.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  return (
    <div ref={anfang} className="detailtafel">
    <Karte
      titel={eigenschaften.titel}
      werkzeuge={
        <Knopf art="still" onClick={eigenschaften.schliessen}>
          Schließen
        </Knopf>
      }
    >
      <div className="stapel">
        {eigenschaften.gesperrt && (
          <Meldung art="neutral">{eigenschaften.gesperrt}</Meldung>
        )}

        <div className="feldreihe">
          {eigenschaften.felder.map((feld) => (
            <label className="feld" key={feld.schluessel}>
              <span className="feld__marke">{feld.marke}</span>
              {feld.art === 'auswahl' ? (
                <select
                  value={werte[feld.schluessel] ?? ''}
                  disabled={
                    feld.schreibgeschuetzt ||
                    eigenschaften.laeuft ||
                    !!eigenschaften.gesperrt
                  }
                  onChange={(e) =>
                    setWerte((b) => ({ ...b, [feld.schluessel]: e.target.value }))
                  }
                >
                  {(feld.optionen ?? []).map((o) => (
                    <option key={o.wert} value={o.wert}>
                      {o.text}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={
                    feld.art === 'zahl'
                      ? 'number'
                      : feld.art === 'datum'
                        ? 'date'
                        : 'text'
                  }
                  value={werte[feld.schluessel] ?? ''}
                  disabled={
                    feld.schreibgeschuetzt ||
                    eigenschaften.laeuft ||
                    !!eigenschaften.gesperrt
                  }
                  onChange={(e) =>
                    setWerte((b) => ({ ...b, [feld.schluessel]: e.target.value }))
                  }
                />
              )}
              {feld.hinweis && (
                <span className="feld__hinweis">{feld.hinweis}</span>
              )}
            </label>
          ))}
        </div>

        {eigenschaften.hinweise}

        {hatAenderung && !eigenschaften.gesperrt && (
          <div className="reihe reihe--rechts">
            <Knopf art="still" onClick={() => setWerte(eigenschaften.werte)}>
              Änderungen verwerfen
            </Knopf>
            <Knopf
              art="haupt"
              disabled={eigenschaften.laeuft}
              onClick={() => eigenschaften.speichern(geaendert)}
            >
              {eigenschaften.laeuft ? 'Wird gespeichert …' : 'Speichern'}
            </Knopf>
          </div>
        )}
      </div>
    </Karte>
    </div>
  );
}
